/**
 * Referral Service
 * Handles referral code management, validation, and tracking
 */

import api from './api';
import logger from '../utils/logger';

class ReferralService {
  constructor() {
    this.endpoints = {
      code: '/api/user/referrals/code',
      stats: '/api/user/referrals/stats',
      list: '/api/user/referrals',
      validate: '/api/public/referrals/validate',
    };
  }

  /**
   * Get or create the user's referral code
   * The auth token is injected automatically by the Axios interceptor in api.js.
   * @returns {Promise<Object>} Referral code response
   */
  async getReferralCode() {
    try {
      logger.info('Getting referral code');

      const response = await api.get(this.endpoints.code);
      return response.data;
    } catch (error) {
      logger.error('Error getting referral code', error);
      throw error;
    }
  }

  /**
   * Get referral statistics for the current user
   * @returns {Promise<Object>} Stats response
   */
  async getReferralStats() {
    try {
      logger.info('Getting referral stats');

      const response = await api.get(this.endpoints.stats);
      return response.data;
    } catch (error) {
      logger.error('Error getting referral stats', error);
      throw error;
    }
  }

  /**
   * Get paginated list of user's referrals
   * @param {number} page - Page number (0-indexed)
   * @param {number} limit - Items per page
   * @returns {Promise<Object>} Referrals list response
   */
  async getReferrals(page = 0, limit = 20) {
    try {
      logger.info('Getting referrals list', { page, limit });

      const response = await api.get(
        `${this.endpoints.list}?page=${page}&limit=${limit}`
      );
      return response.data;
    } catch (error) {
      logger.error('Error getting referrals', error);
      throw error;
    }
  }

  /**
   * Validate a referral code (public endpoint, no auth required)
   * @param {string} code - Referral code to validate
   * @returns {Promise<Object>} Validation response
   */
  async validateCode(code) {
    try {
      logger.info('Validating referral code', { code });

      const response = await api.post(this.endpoints.validate, { code });
      // Always return the response body, even for validation failures
      return response.data;
    } catch (error) {
      logger.error('Error validating referral code', error);
      return {
        valid: false,
        message: 'Error al validar el código',
      };
    }
  }

  /**
   * Store referral code in localStorage (for use after Auth0 callback)
   * @param {string} code - Referral code
   */
  storeReferralCode(code) {
    if (code) {
      localStorage.setItem('rely_referral_code', code.toUpperCase().trim());
      logger.info('Stored referral code', { code });
    }
  }

  /**
   * Get stored referral code from localStorage
   * @returns {string|null} Stored referral code or null
   */
  getStoredReferralCode() {
    return localStorage.getItem('rely_referral_code');
  }

  /**
   * Clear stored referral code from localStorage
   */
  clearStoredReferralCode() {
    localStorage.removeItem('rely_referral_code');
    logger.info('Cleared stored referral code');
  }

  /**
   * Copy referral code to clipboard
   * @param {string} code - Referral code to copy
   * @returns {Promise<boolean>} Success status
   */
  async copyCodeToClipboard(code) {
    try {
      await navigator.clipboard.writeText(code);
      return true;
    } catch (error) {
      logger.error('Failed to copy to clipboard', error);
      return false;
    }
  }

  /**
   * Generate share URL for a referral code
   * @param {string} code - Referral code
   * @param {string} baseUrl - Base URL (defaults to window.location.origin)
   * @returns {string} Share URL
   */
  getShareUrl(code, baseUrl = window.location.origin) {
    return `${baseUrl}/?ref=${code}`;
  }

  /**
   * Generate WhatsApp share URL
   * @param {string} code - Referral code
   * @param {string} message - Optional custom message
   * @returns {string} WhatsApp share URL
   */
  getWhatsAppShareUrl(code, message = null) {
    const defaultMessage = `¡Crea tu LLC en USA con un descuento especial! Usa mi código de referido: ${code}\n\n${this.getShareUrl(
      code
    )}`;
    const text = message || defaultMessage;
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  }

  /**
   * Generate email share URL
   * @param {string} code - Referral code
   * @returns {string} Mailto URL
   */
  getEmailShareUrl(code) {
    const subject = 'Crea tu LLC en USA con TRONA - Código de descuento';
    const body = `¡Hola!\n\nQuiero compartirte esta plataforma para crear tu LLC en Estados Unidos.\n\nUsa mi código de referido para obtener un descuento: ${code}\n\n${this.getShareUrl(
      code
    )}\n\n¡Saludos!`;
    return `mailto:?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  }
}

export default new ReferralService();
